import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useOperationalStore, { getStageLabel } from '../store/useOperationalStore';
import './GlobalSearch.css';

// One typeahead over the client store: RO #, customer name/phone, unit #,
// VIN (last-8 works). Grouped results go straight to the job page; a miss
// that looks like a unit/VIN offers "Create job for this unit" — search is
// also an intake entry point. Client-side only: a shop's live job set is
// hundreds of rows, not millions.

const CLOSED = ['completed', 'voided'];
const MAX_PER_GROUP = 6;

function norm(s) {
  return String(s || '').toLowerCase();
}

function digits(s) {
  return String(s || '').replace(/\D/g, '');
}

function truckMatches(t, q, qDigits) {
  if (norm(t.repairOrder).includes(q)) return true;
  if (norm(t.companyName).includes(q)) return true;
  if (norm(t.contactPerson).includes(q)) return true;
  if (norm(t.truckNumber).includes(q)) return true;
  if (norm(t.vin).includes(q)) return true;
  if (qDigits.length >= 4 && digits(t.phone).includes(qDigits)) return true;
  return false;
}

export default function GlobalSearch({ onAddTruck }) {
  const trucks = useOperationalStore(s => s.trucks);
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // `/` (outside inputs) or Ctrl/Cmd+K focuses search
  useEffect(() => {
    function onKey(e) {
      const inField = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName) || e.target?.isContentEditable;
      if (((e.metaKey || e.ctrlKey) && e.key === 'k') || (e.key === '/' && !inField)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Close on outside click
  useEffect(() => {
    function onMouse(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onMouse);
    return () => document.removeEventListener('mousedown', onMouse);
  }, [open]);

  const q = norm(query.trim());
  const qDigits = digits(query);

  const results = useMemo(() => {
    if (q.length < 2) return { jobs: [], closed: [], customers: [] };
    const jobs = [];
    const closed = [];
    for (const t of trucks) {
      if (!truckMatches(t, q, qDigits)) continue;
      (CLOSED.includes(t.status) ? closed : jobs).push(t);
    }
    // Customers group: distinct companies whose NAME matches (a VIN hit is a
    // job result, not a customer result). Carry the customerId when any of
    // the company's jobs is linked, so navigation is id-stable.
    const companyIds = new Map();
    for (const t of trucks) {
      if (!norm(t.companyName).includes(q)) continue;
      if (!companyIds.has(t.companyName) || (t.customerId && !companyIds.get(t.companyName))) {
        companyIds.set(t.companyName, t.customerId || null);
      }
    }
    const companies = [...companyIds.entries()].map(([name, id]) => ({ name, id }));
    return {
      jobs: jobs.slice(0, MAX_PER_GROUP),
      closed: closed.slice(0, 3),
      customers: companies.slice(0, 4),
    };
  }, [trucks, q, qDigits]);

  // Flattened row list for keyboard navigation, in render order.
  const rows = useMemo(() => ([
    ...results.jobs.map(t => ({ kind: 'job', t })),
    ...results.closed.map(t => ({ kind: 'job', t })),
    ...results.customers.map(c => ({ kind: 'customer', c })),
  ]), [results]);

  const showCreate = q.length >= 3 && rows.length === 0;

  useEffect(() => { setHighlight(0); }, [q]);

  function activate(row) {
    setOpen(false);
    setQuery('');
    if (!row) return;
    if (row.kind === 'job') navigate(`/ro/${row.t.id}`);
    else navigate(`/customers/${encodeURIComponent(row.c.id || row.c.name)}`);
  }

  function handleCreate() {
    setOpen(false);
    setQuery('');
    onAddTruck?.();
  }

  function onInputKey(e) {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, rows.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (rows.length) activate(rows[highlight]);
      else if (showCreate) handleCreate();
    }
  }

  function unitLine(t) {
    const unit = [t.year, t.make, t.model].filter(Boolean).join(' ');
    const bits = [unit || null, t.truckNumber ? `Unit ${t.truckNumber}` : null, t.vin ? `…${String(t.vin).slice(-8)}` : null];
    return bits.filter(Boolean).join(' · ');
  }

  let rowIdx = -1; // running index across groups, matches `rows` order

  return (
    <div className={`gh-search-wrap${focus ? ' gh-search-wrap--focus' : ''}`} ref={wrapRef}>
      <svg className="gh-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input
        ref={inputRef}
        className="gh-search-input"
        type="text"
        placeholder="Search RO #, customer, unit, VIN, phone…"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setFocus(true); if (query) setOpen(true); }}
        onBlur={() => setFocus(false)}
        onKeyDown={onInputKey}
      />
      <span className="gh-search-kbd"><kbd>/</kbd></span>

      {open && q.length >= 2 && (
        <div className="gs-panel">
          {results.jobs.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">Jobs</div>
              {results.jobs.map(t => {
                rowIdx += 1;
                const idx = rowIdx;
                return (
                  <button key={t.id} className={`gs-row${highlight === idx ? ' gs-row--hl' : ''}`}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => activate({ kind: 'job', t })}>
                    <span className="gs-ro">{t.repairOrder}</span>
                    <span className="gs-main">{t.companyName}</span>
                    <span className="gs-sub">{unitLine(t)}</span>
                    <span className="gs-stage">{getStageLabel(t.status)}</span>
                  </button>
                );
              })}
            </div>
          )}

          {results.closed.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">Closed</div>
              {results.closed.map(t => {
                rowIdx += 1;
                const idx = rowIdx;
                return (
                  <button key={t.id} className={`gs-row gs-row--closed${highlight === idx ? ' gs-row--hl' : ''}`}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => activate({ kind: 'job', t })}>
                    <span className="gs-ro">{t.repairOrder}</span>
                    <span className="gs-main">{t.companyName}</span>
                    <span className="gs-sub">{unitLine(t)}</span>
                    <span className="gs-stage">{t.status}</span>
                  </button>
                );
              })}
            </div>
          )}

          {results.customers.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-label">Customers</div>
              {results.customers.map(c => {
                rowIdx += 1;
                const idx = rowIdx;
                return (
                  <button key={c.id || c.name} className={`gs-row${highlight === idx ? ' gs-row--hl' : ''}`}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => activate({ kind: 'customer', c })}>
                    <span className="gs-main">{c.name}</span>
                    <span className="gs-sub">Customer page →</span>
                  </button>
                );
              })}
            </div>
          )}

          {showCreate && (
            <button className="gs-create" onClick={handleCreate}>
              No open job matches “{query.trim()}” — <strong>Create job for this unit →</strong>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
